import { useEffect, useRef, useState } from "react";

export default function Reveal({ children, delay = 0, as: Tag = "div", className = "", ...rest }) {
  const ref = useRef(null);
  // Start visible by default — animation only enhances first paint when JS+IO are ready.
  // This keeps SEO crawlers, headless browsers and reduced-motion users always seeing the content.
  const [animated, setAnimated] = useState(false);
  const [shown, setShown] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    // Re-arm to animate from below
    setShown(false);
    setAnimated(true);
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    // Safety: ensure visible after 1.5s even if IO never fires
    const safety = setTimeout(() => setShown(true), 1500);
    return () => {
      io.disconnect();
      clearTimeout(safety);
    };
  }, []);

  return (
    <Tag
      ref={ref}
      className={`${className} ${animated ? "transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)]" : ""} ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
